import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Table,
  Tbody,
  Tr,
  Td,
  Flex,
  Text,
  useToast,
  Thead,
  Th,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  Button,
} from "@chakra-ui/react";
import { ChevronLeft } from "lucide-react";
import { useLocation, useParams } from "react-router-dom";
import { getBomById } from "../../../redux/api/admin/BomApi";
import { useDispatch, useSelector } from "react-redux";
import { getProduct } from "../../../redux/reduxApi/admin/ProductReduxApi";
import { getRawMaterialList } from "../../../redux/reduxApi/admin/RawMaterialApi";

export default function BOMDetail() {
  const location = useLocation();
  const { id } = useParams();
  const productionOrderId = location.state?.id || id;
  const toast = useToast();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [bomData, setBomData] = useState(null);
  const [error, setError] = useState(null);
  const [bomItemsWithDetails, setBomItemsWithDetails] = useState([]);
  console.log("bomItemsWithDetails",bomItemsWithDetails)
  const [finishedGoodName, setFinishedGoodName] = useState("");

  // Get products from Redux store
  const products = useSelector((state) => state?.inventory?.inventory || []);
  const rawMaterials = useSelector((state) => state?.rawMaterial?.rawMaterial || []);
  const productStatus = useSelector((state) => state?.inventory?.status);
  console.log("rawMaterials", rawMaterials);

  // Fetch products if not available
  useEffect(() => {
    if (!products || products.length === 0) {
      dispatch(getProduct({ limit: 100 }));
    }
  }, [dispatch, products]);

  useEffect(() => {
    if (!rawMaterials || rawMaterials.length === 0) {
      dispatch(getRawMaterialList({}));
    }
  }, [dispatch, rawMaterials]);

  // Parse image URL function
  const parseImageUrl = (itemImages) => {
    if (!itemImages) return '';
    
    try {
      if (typeof itemImages === 'object') {
        if (Array.isArray(itemImages) && itemImages.length > 0) {
          return itemImages[0]?.filePath || itemImages[0]?.url || '';
        } else if (itemImages.filePath) {
          return itemImages.filePath;
        } else if (itemImages.url) {
          return itemImages.url;
        }
      }
      
      if (typeof itemImages === 'string') {
        if (itemImages.startsWith('[') || itemImages.startsWith('{')) {
          const parsed = JSON.parse(itemImages);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed[0]?.filePath || parsed[0]?.url || '';
          } else if (parsed.filePath) {
            return parsed.filePath;
          } else if (parsed.url) {
            return parsed.url;
          }
        }
        
        if (itemImages.startsWith('http')) {
          return itemImages;
        }
      }
      
      return '';
    } catch (error) {
      console.error('Error parsing image:', error);
      return '';
    }
  };

  // Fetch BOM data
  useEffect(() => {
    const fetchBomData = async () => {
      if (!productionOrderId) {
        setError("No BOM ID provided");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await getBomById(productionOrderId);
        const fetchedData = response.data?.data;
        if (fetchedData) {
          // Transform the data to combine bomMaster and bomItem
          const transformedData = {
            ...fetchedData.bomMaster,
            bomItems: fetchedData.bomItem || []
          };
          setBomData(transformedData);
          console.log("Transformed BOM data:", transformedData);
        } else {
          setError("No BOM data found");
        }
      } catch (error) {
        console.error("Error fetching BOM:", error);
        setError(error.response?.data?.message || "Failed to fetch BOM data");
        toast({
          title: "Error",
          description: error.response?.data?.message || "Failed to fetch BOM data",
          status: "error",
          duration: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBomData();
  }, [productionOrderId, toast]);

  // Get finished good item name
  useEffect(() => {
    if (bomData?.item && products.length > 0) {
      const finishedGood = products.find(p => p.ROWID === bomData.item);
      if (finishedGood) {
        setFinishedGoodName(finishedGood.itemName || finishedGood.name || bomData.item);
      } else {
        setFinishedGoodName(bomData.item);
      }
    }
  }, [bomData, products]);


  // Enrich bomItems with product details
  useEffect(() => {
    if (bomData?.bomItems && rawMaterials.length > 0) {
      const enrichedItems = bomData.bomItems.map(item => {
        const productDetail = rawMaterials.find(p => p.ROWID === item.productId);
        return {
          ...item,
          productName: productDetail?.productName ,
          itemImages: JSON.parse(productDetail?.img)[0],
          unit: productDetail?.unit,
        };
      });
      setBomItemsWithDetails(enrichedItems);
      console.log("Enriched BOM items:", enrichedItems);
    }
  }, [bomData, rawMaterials]);

  // Show loading state
  if (isLoading || productStatus === 'pending') {
    return (
      <Box className="h-[93vh] flex flex-col">
        <Box className="sticky top-0 z-10 bg-white shadow-md py-2 ps-2">
          <div className="d-flex gap-2 justify-start items-center">
            <ChevronLeft 
              onClick={() => window.history.back()} 
              className="hover:bg-gray-200 rounded-full cursor-pointer"
            />
            <h4 className="mb-0">BOM Detail</h4>
          </div>
        </Box>
        <Flex justifyContent="center" alignItems="center" height="100%">
          <Spinner size="xl" color="#011638" thickness="4px" />
        </Flex>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box className="h-[93vh] flex flex-col">
        <Box className="sticky top-0 z-10 bg-white shadow-md py-2 ps-2">
          <div className="d-flex gap-2 justify-start items-center">
            <ChevronLeft 
              onClick={() => window.history.back()} 
              className="hover:bg-gray-200 rounded-full cursor-pointer"
            />
            <h4 className="mb-0">BOM Detail</h4>
          </div>
        </Box>
        <Flex justifyContent="center" alignItems="center" height="100%" p={6}>
          <Alert status="error" width="500px" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <Heading size="sm" mb={2}>Error Loading BOM</Heading>
              <p>{error}</p>
              <Button 
                size="sm" 
                colorScheme="blue" 
                mt={3}
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </Box>
          </Alert>
        </Flex>
      </Box>
    );
  }

  return (
    <Box className="h-[93vh] flex flex-col mb-20 overflow-y-auto">
      <Box className="sticky top-0 z-10 bg-white shadow-md py-2 ps-2">
        <div className="d-flex gap-2 justify-start items-center">
          <ChevronLeft 
            onClick={() => window.history.back()} 
            className="hover:bg-gray-200 rounded-full cursor-pointer"
          />
          <h4 className="mb-0">BOM Detail</h4>
        </div>
      </Box>

      <Box className="flex-1 overflow-y-auto p-6">
        <Flex gap={6} direction={{ base: "column", md: "row" }} mt={7} flexWrap="wrap">
          <Box flex="1" p={4} borderWidth="1px" borderRadius="lg" boxShadow="sm" bg="white" width={{ base: "100%", md: "50%" }}>
            <Heading as="h3" size="md" mb={4}>
              BOM Information
            </Heading>
            <Table variant="simple" size="sm">
              <Tbody>
                <Tr>
                  <Td fontWeight="bold" width="40%">BOM</Td>
                  <Td>{bomData?.bom || '-'}</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="bold">Item</Td>
                  <Td>
                    <div>
                      <div> {finishedGoodName || '-'}</div>
                    </div>
                  </Td>
                </Tr>
                <Tr>
                  <Td fontWeight="bold">Version</Td>
                  <Td>{bomData?.version || '-'}</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="bold">Description</Td>
                  <Td>{bomData?.description || '-'}</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="bold">Created Time</Td>
                  <Td>{bomData?.CREATEDTIME ? new Date(bomData.CREATEDTIME).toLocaleString() : '-'}</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="bold">Modified Time</Td>
                  <Td>{bomData?.MODIFIEDTIME ? new Date(bomData.MODIFIEDTIME).toLocaleString() : '-'}</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </Flex>

        <Box
          mt={6}
          flex="1"
          px={2}
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="sm"
          bg="white"
          maxHeight="500px"
          display="flex"
          flexDirection="column"
        >
          <Box borderBottom="1px" borderColor="gray.200" px={4} py={2} flexShrink={0}>
            <Text fontWeight="bold" fontSize="md">
              Item Details
            </Text>
          </Box>
          
          {/* Table with fixed header and scrollable body */}
          <Box flex="1" overflow="auto" position="relative">
            <Table size="sm">
              <Thead position="sticky" top={0} zIndex="sticky" bg="gray.50">
                <Tr>
                  <Th className="w-20">Sr no.</Th>
                  <Th className="w-50">Items</Th>
                  <Th className="whitespace-nowrap">Material Image</Th>
                  <Th className="whitespace-nowrap">UOM</Th>
                </Tr>
              </Thead>
              <Tbody>
                {bomItemsWithDetails && bomItemsWithDetails.length > 0 ? (
                  bomItemsWithDetails.map((item, index) => (
                    <Tr key={item.ROWID || index}>
                      <Td className="whitespace-nowrap w-10">
                        <div>
                          <div> {index+1}</div>
                        </div>
                      </Td>
                      <Td className="whitespace-nowrap">
                        <div>
                          <div> {item.productName || '-'}</div>
                        </div>
                      </Td>
                      <Td className="whitespace-nowrap">
                        <img 
                          className="h-30 w-32 p-2 border object-cover rounded-md" 
                          src={parseImageUrl(item.itemImages) || 'https://via.placeholder.com/150?text=No+Image'} 
                          alt={item.productName || 'Item image'}
                        />
                      </Td>
                      <Td className="whitespace-nowrap">
                        <div>
                          <div>{item.uom || '-'}</div>
                        </div>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={4} textAlign="center" py={4}>
                      No BOM items found
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}